import React, { useState, useEffect } from "react";
import { useDebounce } from "@/hooks/use-debounce";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useLocation } from "wouter";
import {
  useListAdminFeedbacks,
  getListAdminFeedbacksQueryKey,
  useListAdminRatedAssessments,
  getListAdminRatedAssessmentsQueryKey,
} from "@workspace/api-client-react";
import type { AdminFeedbackItem, AdminRatedAssessment } from "@workspace/api-client-react";
import { useAdminAuth } from "@/contexts/admin-auth";
import AdminLayout from "@/components/admin-layout";

import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Star, MessageCircle, ExternalLink, Download } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronDown, ChevronUp } from "lucide-react";

function escapeCsvField(value: string | number | null | undefined): string {
  if (value == null) return "";
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function downloadCsv(filename: string, headers: string[], rows: (string | number | null | undefined)[][]) {
  const lines = [
    headers.map(escapeCsvField).join(","),
    ...rows.map((row) => row.map(escapeCsvField).join(",")),
  ];
  const bom = "\uFEFF";
  const blob = new Blob([bom + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function StarRating({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`h-3.5 w-3.5 ${n <= rating ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30"}`}
        />
      ))}
      <span className="ml-1 text-sm font-medium">{rating}/5</span>
    </span>
  );
}

function FeedbackRow({ item }: { item: AdminFeedbackItem }) {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible asChild open={open} onOpenChange={setOpen}>
      <>
        <TableRow
          className="cursor-pointer hover:bg-muted/30"
          onClick={() => setOpen((v) => !v)}
          data-testid={`row-feedback-${item.id}`}
        >
          <TableCell className="pl-6 text-sm font-medium">{item.name}</TableCell>
          <TableCell className="text-sm">{item.company}</TableCell>
          <TableCell className="text-sm">{item.email}</TableCell>
          <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
            {item.message}
          </TableCell>
          <TableCell className="text-sm text-muted-foreground">
            {format(new Date(item.submittedAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}
          </TableCell>
          <TableCell className="pr-4 text-right">
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={(e) => e.stopPropagation()}>
                {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>
          </TableCell>
        </TableRow>
        <CollapsibleContent asChild>
          <TableRow className="bg-muted/20 hover:bg-muted/20">
            <TableCell colSpan={6} className="px-6 py-4">
              <p className="text-sm whitespace-pre-wrap text-foreground leading-relaxed">{item.message}</p>
            </TableCell>
          </TableRow>
        </CollapsibleContent>
      </>
    </Collapsible>
  );
}

function GeneralFeedbackTab() {
  const { logout } = useAdminAuth();
  const [nameInput, setNameInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const debouncedName = useDebounce(nameInput, 400);
  const debouncedEmail = useDebounce(emailInput, 400);

  const params: Record<string, string> = {};
  if (debouncedName) params.name = debouncedName;
  if (debouncedEmail) params.email = debouncedEmail;

  const { data: feedbacks, isLoading, error } = useListAdminFeedbacks(params, {
    query: { queryKey: getListAdminFeedbacksQueryKey(params) },
  });

  useEffect(() => {
    if (error && (error as { status?: number }).status === 401) logout();
  }, [error, logout]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end justify-between">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Nome</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                data-testid="filter-feedback-name"
                placeholder="Buscar por nome"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                className="pl-8 h-9 w-56 text-sm"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">E-mail</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                data-testid="filter-feedback-email"
                placeholder="Buscar por e-mail"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                className="pl-8 h-9 w-56 text-sm"
              />
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-2"
          disabled={!feedbacks || feedbacks.length === 0}
          onClick={() => {
            if (!feedbacks) return;
            downloadCsv(
              `feedbacks-${new Date().toISOString().slice(0, 10)}.csv`,
              ["Nome", "Empresa", "E-mail", "Mensagem", "Data"],
              feedbacks.map((f) => [f.name, f.company, f.email, f.message, f.submittedAt]),
            );
          }}
          data-testid="export-feedbacks-csv"
        >
          <Download className="h-4 w-4" />
          Exportar CSV
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-6">Nome</TableHead>
              <TableHead>Empresa</TableHead>
              <TableHead>E-mail</TableHead>
              <TableHead>Mensagem</TableHead>
              <TableHead>Data</TableHead>
              <TableHead className="pr-4 text-right w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 5 }).map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                  <TableCell />
                </TableRow>
              ))
            ) : feedbacks && feedbacks.length > 0 ? (
              feedbacks.map((item) => <FeedbackRow key={item.id} item={item} />)
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-muted-foreground text-sm">
                  {nameInput || emailInput ? "Nenhum resultado para os filtros aplicados." : "Nenhum feedback recebido ainda."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function RatedAssessmentRow({ session }: { session: AdminRatedAssessment }) {
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  const hasComment = !!session.participantRatingComment;

  return (
    <Collapsible asChild open={open} onOpenChange={setOpen}>
      <>
        <TableRow
          className="cursor-pointer hover:bg-muted/30"
          onClick={() => setOpen((v) => !v)}
          data-testid={`row-rated-session-${session.id}`}
        >
          <TableCell className="pl-6 text-sm font-medium">
            {session.participantName ?? <span className="text-muted-foreground">—</span>}
          </TableCell>
          <TableCell className="text-sm">
            {session.participantEmail ?? <span className="text-muted-foreground">—</span>}
          </TableCell>
          <TableCell>
            <StarRating rating={session.participantRating} />
          </TableCell>
          <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
            {hasComment
              ? <span className="text-foreground">{session.participantRatingComment}</span>
              : <span className="text-muted-foreground">—</span>}
          </TableCell>
          <TableCell className="text-sm text-muted-foreground">
            {session.ratedAt
              ? format(new Date(session.ratedAt), "dd/MM/yyyy HH:mm", { locale: ptBR })
              : "—"}
          </TableCell>
          <TableCell className="pr-4 text-right">
            <div className="flex items-center justify-end gap-1">
              {hasComment && (
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                </CollapsibleTrigger>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/sessions/${session.id}`);
                }}
              >
                <ExternalLink className="h-3.5 w-3.5 mr-1" />
                Ver sessão
              </Button>
            </div>
          </TableCell>
        </TableRow>
        {hasComment && (
          <CollapsibleContent asChild>
            <TableRow className="bg-muted/20 hover:bg-muted/20">
              <TableCell colSpan={6} className="px-6 py-4">
                <p className="text-sm whitespace-pre-wrap text-foreground leading-relaxed">
                  {session.participantRatingComment}
                </p>
              </TableCell>
            </TableRow>
          </CollapsibleContent>
        )}
      </>
    </Collapsible>
  );
}

const RATING_OPTIONS = [
  { value: "any", label: "Qualquer" },
  { value: "1", label: "1 ★" },
  { value: "2", label: "2 ★" },
  { value: "3", label: "3 ★" },
  { value: "4", label: "4 ★" },
  { value: "5", label: "5 ★" },
];

function SessionRatingsTab() {
  const { logout } = useAdminAuth();
  const [nameInput, setNameInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [commentInput, setCommentInput] = useState("");
  const [minRating, setMinRating] = useState<string>("any");
  const [maxRating, setMaxRating] = useState<string>("any");
  const debouncedName = useDebounce(nameInput, 400);
  const debouncedEmail = useDebounce(emailInput, 400);
  const debouncedComment = useDebounce(commentInput, 400);

  const params: Record<string, string> = {};
  if (debouncedName) params.name = debouncedName;
  if (debouncedEmail) params.email = debouncedEmail;
  if (debouncedComment) params.comment = debouncedComment;
  if (minRating !== "any") params.minRating = minRating;
  if (maxRating !== "any") params.maxRating = maxRating;

  const { data: sessions, isLoading, error } = useListAdminRatedAssessments(params, {
    query: { queryKey: getListAdminRatedAssessmentsQueryKey(params) },
  });

  useEffect(() => {
    if (error && (error as { status?: number }).status === 401) logout();
  }, [error, logout]);

  const hasFilters = nameInput || emailInput || commentInput || minRating !== "any" || maxRating !== "any";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end justify-between">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Nome</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                data-testid="filter-ratings-name"
                placeholder="Buscar por nome"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                className="pl-8 h-9 w-48 text-sm"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">E-mail</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                data-testid="filter-ratings-email"
                placeholder="Buscar por e-mail"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                className="pl-8 h-9 w-48 text-sm"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Comentário</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                data-testid="filter-ratings-comment"
                placeholder="Buscar no comentário"
                value={commentInput}
                onChange={(e) => setCommentInput(e.target.value)}
                className="pl-8 h-9 w-48 text-sm"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Nota mínima</label>
            <Select value={minRating} onValueChange={setMinRating}>
              <SelectTrigger data-testid="filter-ratings-min" className="h-9 w-28 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RATING_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Nota máxima</label>
            <Select value={maxRating} onValueChange={setMaxRating}>
              <SelectTrigger data-testid="filter-ratings-max" className="h-9 w-28 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RATING_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-2"
          disabled={!sessions || sessions.length === 0}
          onClick={() => {
            if (!sessions) return;
            downloadCsv(
              `avaliacoes-sessao-${new Date().toISOString().slice(0, 10)}.csv`,
              ["Participante", "E-mail", "Nota", "Comentário", "Data"],
              sessions.map((s) => [
                s.participantName,
                s.participantEmail,
                s.participantRating,
                s.participantRatingComment,
                s.ratedAt,
              ]),
            );
          }}
          data-testid="export-ratings-csv"
        >
          <Download className="h-4 w-4" />
          Exportar CSV
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-6">Participante</TableHead>
              <TableHead>E-mail</TableHead>
              <TableHead>Avaliação</TableHead>
              <TableHead>Comentário</TableHead>
              <TableHead>Data</TableHead>
              <TableHead className="pr-4 text-right w-36"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 5 }).map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                  <TableCell />
                </TableRow>
              ))
            ) : sessions && sessions.length > 0 ? (
              sessions.map((s: AdminRatedAssessment) => <RatedAssessmentRow key={s.id} session={s} />)
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-muted-foreground text-sm">
                  {hasFilters ? "Nenhum resultado para os filtros aplicados." : "Nenhuma avaliação de sessão recebida ainda."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function FeedbacksContent() {
  const { data: allFeedbacks } = useListAdminFeedbacks({}, {
    query: { queryKey: getListAdminFeedbacksQueryKey({}) },
  });
  const { data: allRatedSessions } = useListAdminRatedAssessments({}, {
    query: { queryKey: getListAdminRatedAssessmentsQueryKey({}) },
  });

  const totalFeedbacks = allFeedbacks?.length ?? 0;
  const totalRatings = allRatedSessions?.length ?? 0;
  const averageRating =
    allRatedSessions && allRatedSessions.length > 0
      ? allRatedSessions.reduce((sum: number, s: AdminRatedAssessment) => sum + s.participantRating, 0) / allRatedSessions.length
      : null;
  const totalWithComment = allRatedSessions
    ? allRatedSessions.filter((s: AdminRatedAssessment) => !!s.participantRatingComment).length
    : null;

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight mb-1">Feedbacks</h1>
        <p className="text-muted-foreground">Feedbacks gerais do produto e avaliações de sessões de entrevista.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="shadow-sm">
          <CardContent className="py-4 px-6">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Feedbacks gerais</p>
            <p className="text-2xl font-semibold mt-1" data-testid="stat-total-feedbacks">
              {allFeedbacks === undefined ? <Skeleton className="h-7 w-12 inline-block" /> : totalFeedbacks}
            </p>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="py-4 px-6">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Avaliações de sessão</p>
            <p className="text-2xl font-semibold mt-1" data-testid="stat-total-ratings">
              {allRatedSessions === undefined ? <Skeleton className="h-7 w-12 inline-block" /> : totalRatings}
            </p>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-yellow-200 bg-yellow-50/30">
          <CardContent className="py-4 px-6 flex items-center gap-3">
            <Star className="h-5 w-5 fill-yellow-400 text-yellow-400 shrink-0" />
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Avaliação média</p>
              {allRatedSessions === undefined ? (
                <Skeleton className="h-7 w-20 mt-1" />
              ) : averageRating != null ? (
                <p className="text-2xl font-semibold mt-1" data-testid="stat-avg-rating">
                  {averageRating.toFixed(1)}
                  <span className="text-sm font-normal text-muted-foreground">/5</span>
                </p>
              ) : (
                <p className="text-sm text-muted-foreground mt-1" data-testid="stat-avg-rating">—</p>
              )}
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-blue-100 bg-blue-50/20">
          <CardContent className="py-4 px-6 flex items-center gap-3">
            <MessageCircle className="h-5 w-5 text-blue-400 shrink-0" />
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Com comentário</p>
              {allRatedSessions === undefined ? (
                <Skeleton className="h-7 w-12 mt-1" />
              ) : (
                <p className="text-2xl font-semibold mt-1" data-testid="stat-with-comment">
                  {totalWithComment}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general" data-testid="tab-general-feedback">
            <MessageCircle className="h-4 w-4 mr-2" />
            Feedbacks Gerais
          </TabsTrigger>
          <TabsTrigger value="ratings" data-testid="tab-session-ratings">
            <Star className="h-4 w-4 mr-2" />
            Avaliações de Sessão
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-6">
          <GeneralFeedbackTab />
        </TabsContent>

        <TabsContent value="ratings" className="mt-6">
          <SessionRatingsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default function FeedbacksPage() {
  return (
    <AdminLayout>
      <FeedbacksContent />
    </AdminLayout>
  );
}
